"use client";

import type { Table } from "@tanstack/react-table";
import { ChevronsUpDown, Settings2 } from "lucide-react";
import * as React from "react";
import { Button } from "../components/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "../components/command";
import { Popover, PopoverContent, PopoverTrigger } from "../components/popover";

interface DataTableViewOptionsProps<TData> {
	table: Table<TData>;
}

export function DataTableViewOptions<TData>({
	table,
}: DataTableViewOptionsProps<TData>) {
	// Reads visibility state off the mutable table instance. See DataTable.
	"use no memo";

	const columns = React.useMemo(
		() =>
			table
				.getAllColumns()
				.filter(
					(column) =>
						typeof column.accessorFn !== "undefined" && column.getCanHide(),
				),
		[table],
	);

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					aria-label="Toggle columns"
					variant="outline"
					size="sm"
					className="ml-auto hidden h-8 lg:flex"
				>
					<Settings2 />
					View
					<ChevronsUpDown className="ml-auto opacity-50" />
				</Button>
			</PopoverTrigger>
			{/* Grows with the longest column name instead of clipping it. */}
			<PopoverContent
				align="end"
				className="w-auto min-w-48 max-w-[min(20rem,var(--radix-popover-content-available-width))] p-0"
			>
				<Command>
					<CommandInput placeholder="Search columns..." />
					<CommandList>
						<CommandEmpty>No columns found.</CommandEmpty>
						<CommandGroup>
							{columns.map((column) => (
								// `checked` rather than a check of our own: CommandItem already
								// ends every row with one, and two `ml-auto` siblings split the
								// free space between them, which parked the visible tick in the
								// middle of the row instead of against its right edge.
								<CommandItem
									key={column.id}
									checked={column.getIsVisible()}
									onSelect={() =>
										column.toggleVisibility(!column.getIsVisible())
									}
								>
									<span className="truncate">
										{column.columnDef.meta?.label ?? column.id}
									</span>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
